import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import {Formik, Field, Form, ErrorMessage} from "formik";
import {useNavigate, useParams} from "react-router-dom";
import {Container, Row, Col, Button, Card} from "react-bootstrap";
import * as Yup from "yup";
import ConfirmationModal from '../shared/ConfirmationModal';
import NewconfirmationModal from '../shared/NewconfirmationModal';
import AllFailureModal from '../shared/AllFailureModal';
import {CitySearchDropdown} from "./JobDetailsUpdate";

const SkillsAutocomplete = ({value, onChange, onBlur, skillsList}) => {
  const initialValue = Array.isArray(value) ? value : value ? value?.split(",") : [];
  const [inputValue, setInputValue] = useState('');
  const [selectedSkills, setSelectedSkills] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedSkills]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.trim()) {
      const filtered = skillsList.filter(skill => {
        const searchTerm = value.toLowerCase();
        const alreadySelected = selectedSkills.some(
          selected => selected.skill_id === skill.skill_id
        );

        return !alreadySelected && (
          skill.skill_name.toLowerCase().includes(searchTerm) ||
          skill.skill_shortname.toLowerCase().includes(searchTerm) ||
          skill.skill_tag.toLowerCase().includes(searchTerm)
        );
      });
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const addSkill = (skill) => {
    if (!selectedSkills.some(selected => selected.skill_id === skill.skill_id)) {
      const newSkills = [...selectedSkills, skill];
      setSelectedSkills(newSkills);
      onChange(newSkills);
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const removeSkill = (skillToRemove) => {
    const newSkills = selectedSkills.filter(
      skill => skill.skill_id !== skillToRemove.skill_id
    );
    setSelectedSkills(newSkills);
    onChange(newSkills);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim() !== '') {
      e.preventDefault();
      if (suggestions.length > 0) {
        addSkill(suggestions[0]);
      }
    }
  };

  const renderSkillBadge = (skill) => {
    const tags = skill?.skill_tag?.split(',').slice(0, 2).map(tag => tag.trim());
    return (
      <span
        key={skill.skill_id}
        className="badge bg-primary me-2 mb-2 d-inline-flex align-items-center"
      >
        <span className="d-flex flex-column">
          <span>{skill.skill_name}</span>
          <small className="text-white-50">
            {tags?.join(' • ')}
          </small>
        </span>
        <button
          type="button"
          className="btn-close btn-close-white ms-2"
          style={{fontSize: '0.5rem'}}
          onClick={() => removeSkill(skill)}
          aria-label="Remove skill"
        />
      </span>
    );
  };

  const renderSuggestionItem = (skill) => {
    return (
      <div
        key={skill.skill_id}
        className="p-2 cursor-pointer table-hover border rounded"
        style={{cursor: 'pointer'}}
        onMouseDown={() => addSkill(skill)}
      >
        <div className="d-flex flex-column">
          <span className="fw-medium">{skill.skill_name}</span>
          <small className="text-muted">
            {skill.skill_category} • {skill.skill_shortname}
          </small>
          <small className="text-muted">
            Tags: {skill.skill_tag}
          </small>
          <hr/>
        </div>
      </div>
    );
  };

  return (
    <div className="skills-autocomplete">
      <div className="mb-2">
        {selectedSkills.map(renderSkillBadge)}
      </div>
      <div className="position-relative">
        <input
          type="text"
          ref={inputRef}
          className="form-control"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setTimeout(() => setShowSuggestions(false), 200);
            onBlur?.();
          }}
          placeholder="Search skills by name, shortname, or tags..."
        />
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="position-absolute w-100 mt-1 shadow bg-white rounded border"
            style={{
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}
          >
            {suggestions ? suggestions.map(renderSuggestionItem) : <span className="text-muted">Skills not found</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const JobDetailsAdd = () => {
  const {jobid} = useParams();
  const [skills, setSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [degrees, setDegrees] = useState([]);

  const jobDetailsInitialState = {
    job_id: jobid,
    number_of_positions: 0,
    job_highlights: "",
    job_description: "",
    skills: "",
    department: "",
    city: "",
    package_: "",
    role: "",
    qualification: "",
    experience: "",
    job_type: "",
    job_application_internal: "",
    job_external_url: ""
  };

  const jobTypes = [
    {value: '', label: 'Select Job Type'},
    {value: 'full_time', label: 'Full Time'},
    {value: 'part_time', label: 'Part Time'},
    {value: 'contract', label: 'Contract'},
    {value: 'internship', label: 'Internship'},
    {value: 'freelance', label: 'Freelance'},
    {value: 'temporary', label: 'Temporary'},
    {value: 'remote', label: 'Remote'},
    {value: 'NA', label: 'NA'},
  ];

  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    number_of_positions: Yup.number()
      .required('Number of positions is required')
      .min(1, 'Must be at least 1 position')
      .integer('Must be a whole number'),
    skills: Yup.mixed().required('Skills are required'),
    role: Yup.string().nullable(),
    job_type: Yup.string().required('Job type is required'),
    job_description: Yup.string().required('Job description is required'),
    package_: Yup.string().nullable(),
    qualification: Yup.string().required('Qualification is required'),
    experience: Yup.string().required("Experience is required"),
    city: Yup.string().required('City is required'),
    department: Yup.string().nullable(),
    job_highlights: Yup.string(),
    job_application_internal: Yup.string(),
    job_external_url: Yup.string().url('Must be a valid URL'),
    work_mode: Yup.string().required("Work mode is required")
  });

  const callApiJobAdd = async (values) => {
    try {
      const url = process.env.REACT_APP_API_URL + "job_details/createjob_details";
      const response = await axios.post(url, values);
      setSuccessMessage("Job details added successfully!");
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate("/joblist");
      }, 3000);
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to add job details. Please try again.");
      setShowFailureModal(true);
    }
  };
  const handleSubmitWithConfirmation = (values) => {
    setModalMessage("Are you sure you want to add these job details?");
    setPendingSubmitData(values);
    setModalShow(true);
  };

  const handleModalOk = () => {
    setModalShow(false);
    if (pendingSubmitData) {
      callApiJobAdd(pendingSubmitData);
      setPendingSubmitData(null);
    }
  };
  const fetchAllSkills = async () => {
    try {
      const url = process.env.REACT_APP_API_URL + "skills/showallskills";
      const response = await axios.get(url);
      setSkills(response.data);
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to fetch skills. Please refresh the page.");
      setShowFailureModal(true);
    }
  }

  const fetchAllDepartments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}department/getdepartments`);
      setDepartments(response.data);
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to fetch departments. Please refresh the page.");
      setShowFailureModal(true);
    }
  }
  const fetchAllDegrees = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}degree/getdegrees`);
      setDegrees(response.data);
    } catch (error) {
      console.log(error);
      setFailureMessage("Failed to fetch departments. Please refresh the page.");
      setShowFailureModal(true);
    }
  }

  useEffect(() => {
    fetchAllSkills();
    fetchAllDepartments();
    fetchAllDegrees()
  }, []);

  return (
    <Container className="mt-5">
      <Row className="justify-content-center w-110">
        <Col md={8}>
          <Card className="shadow">
            <Card.Header className="bg-info text-white text-center">
              <h4>Add More About Job</h4>
            </Card.Header>
            <Card.Body>
              <Formik
                enableReinitialize={true}
                initialValues={jobDetailsInitialState}
                validationSchema={validationSchema}
                onSubmit={handleSubmitWithConfirmation}
              >
                {() => (
                  <Form>
                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="no" className="form-label">
                          No Of Positions:<span className="text-danger">*</span>
                        </label>
                        <Field
                          name="number_of_positions"
                          type="number"
                          className="form-control"
                          placeholder="Enter Number of Positions"
                        />
                        <ErrorMessage
                          name="number_of_positions"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>

                      <Col md={6}>
                        <label htmlFor="job_type" className="form-label">
                          Job Type:<span className="text-danger">*</span>
                        </label>
                        <Field as="select" name="job_type" className="form-control">
                          {jobTypes.map((type) => (
                            <option key={type.value} value={type.label}>
                              {type.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="job_type"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="skills" className="form-label">
                          Skills:<span className="text-danger">*</span>
                        </label>
                        <Field>
                          {({field, form}) => (
                            <SkillsAutocomplete
                              value={[]}
                              onChange={(skills) => form.setFieldValue('skills', skills)}
                              onBlur={field.onBlur}
                              skillsList={skills}
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          name="skills"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>


                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="qualification" className="form-label">
                          Qualification:<span className="text-danger">*</span>
                        </label>
                        <Field
                          as="select"
                          name="qualification"
                          className="form-control"
                        >
                          <option value="">--Choose Highest Qualification--</option>
                          {degrees.map((degree) => (
                            <option value={degree.degree_name} key={degree.d_id}>{degree.degree_name}</option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="qualification"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="work_mode" className="form-label">
                          Work Mode:
                        </label><span className="text-danger">*</span>
                        <Field as="select" name="work_mode" className="form-control">
                          <option value="">--Choose Work Mode--</option>
                          <option value="wfh">Work From Home</option>
                          <option value="wfo">Work From Office</option>
                          <option value="hybrid">Hybrid</option>
                        </Field>
                        <ErrorMessage
                          name="work_mode"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3 mt-2">
                      <Col md={6}>
                        <label htmlFor="department" className="form-label">
                          Department:
                        </label>
                        <Field
                          as="select"
                          name="department"
                          className="form-control"
                          placeholder="Enter Department"
                        >
                          <option value="">--Choose Department--</option>
                          {departments.map((department) => (
                            <option key={department.department_id}
                                    value={department.department_name}>{department.department_name}</option>
                          ))}
                        </Field>
                        {/* <ErrorMessage
                          name="department"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>
                      <Col md={6}>
                        <label htmlFor="role" className="form-label">
                          Role Name:
                        </label>
                        <Field
                          name="role"
                          type="text"
                          className="form-control"
                          placeholder="Enter Job Role"
                        />
                        {/* <ErrorMessage
                          name="role"
                          component="div"
                          className="text-danger mt-1"
                        /> */}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="experience" className="form-label">Experience:</label>
                        <span className="text-danger">*</span>
                        <Field
                          name="experience"
                          type="text"
                          className="form-control"
                          placeholder="Enter Experience Required (e.g. 4-5 Years)"
                        />
                        <ErrorMessage
                          name="experience"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="job_external_url" className="form-label">
                          Job External URL:
                        </label>
                        <Field
                          name="job_external_url"
                          type="text"
                          className="form-control"
                          placeholder="Enter ..."
                        />
                        <ErrorMessage
                          name="job_external_url"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <label htmlFor="package_" className="form-label">
                          Package:
                        </label><span className="text-danger">*</span>
                        <Field
                          name="package_"
                          type="text"
                          className="form-control"
                          placeholder=" e.g. 4 - 5 LPA"
                        />
                         <ErrorMessage
                          name="package_"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                      <Col md={6}>
                        <label htmlFor="city" className="form-label">City:</label><span className="text-danger">*</span>
                        <Field
                          name="city"
                          type="text"
                          className="form-control"
                          placeholder="Enter complete address"
                        >
                          {({field, form}) => (
                            <CitySearchDropdown name={field.name}/>
                          )}
                        </Field>
                        <ErrorMessage
                          name="address"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="job_description" className="form-label">
                          Job Description:<span className="text-danger">*</span>
                        </label>
                        <Field
                          as="textarea"
                          name="job_description"
                          type="text"
                          className="form-control"
                          rows="3"
                        />
                        <ErrorMessage
                          name="job_description"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <label htmlFor="job_highlights" className="form-label">
                          Job Highlights:
                        </label>
                        <Field
                          as="textarea"
                          rows="3"
                          name="job_highlights"
                          type="text"
                          className="form-control"
                        />
                        <ErrorMessage
                          name="job_highlights"
                          component="div"
                          className="text-danger mt-1"
                        />
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col className="text-center">
                        <Button type="submit" variant="primary">
                          Add Details
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
              <ConfirmationModal
                show={modalShow}
                modalMessage={modalMessage}
                onHide={() => setModalShow(false)}
                confirmation={handleModalOk}
                operationType="Confirm"
              />
              <NewconfirmationModal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}
                                    modalMessage={successMessage} operationType="Ok" timeoutDuration={3000}/>
              <AllFailureModal show={showFailureModal} onHide={() => setShowFailureModal(false)}
                               modalMessage={failureMessage} operationType="Ok" timeoutDuration={5000}/>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default JobDetailsAdd;